import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';
import { billingApi, type CheckoutSessionDetail } from '../api/services';
import BrandMark from '../components/BrandMark';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// OnboardingSuccessPage — confirmation surface after Stripe Checkout. Lands
// on the rebranded Stratium dark backdrop (matches SignupPage / LoginPage so
// the auth surfaces read as one moment), looks up the Stripe session by id
// to render a receipt block (plan name, seat count, masked email, short
// reference), and clearly tells the user to check their email for the
// password-set link.
//
// Three states:
//   1. session_id present + session is complete — full receipt
//   2. session_id present but Stripe says it isn't paid yet — soft
//      "still processing" state
//   3. session_id missing or lookup failed — generic confirmation
//      with no receipt (someone navigated direct to /onboarding/success)
// ---------------------------------------------------------------------------

const FIELD_LABEL_CLASS = 'text-[12.5px] tracking-[0.02em] text-[rgba(234,238,246,0.40)]';

export default function OnboardingSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id') ?? '';

  const [session, setSession] = useState<CheckoutSessionDetail | null>(null);
  const [lookupState, setLookupState] = useState<'idle' | 'loading' | 'success' | 'failed' | 'no-id'>(
    sessionId ? 'loading' : 'no-id',
  );

  useEffect(() => {
    const prev = document.title;
    document.title = 'Welcome to Cadence';
    return () => { document.title = prev; };
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const detail = await billingApi.getCheckoutSession(sessionId);
        if (cancelled) return;
        setSession(detail);
        // status: "complete" means Stripe captured payment AND the
        // session is fully done; "open" / "expired" / null all read as
        // not-yet-complete. payment_status: "paid" or "no_payment_required"
        // (trial-only) are the success values.
        const isComplete =
          detail.status === 'complete' &&
          (detail.paymentStatus === 'paid' || detail.paymentStatus === 'no_payment_required');
        setLookupState(isComplete ? 'success' : 'failed');
      } catch {
        if (!cancelled) setLookupState('failed');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="dark relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070A12]">
      {/* Ambient backdrop — same copper halo + cool blue top-glow +
          vignette pattern Signup uses so the post-payment moment reads
          as continuous with the signup flow rather than a return to an
          older surface. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
        <div
          className="absolute left-1/2 bottom-[-24%] h-[78vh] w-[120vw] -translate-x-1/2 rounded-full blur-[14px]"
          style={{ background: 'radial-gradient(ellipse at center, rgba(255,176,122,0.14) 0%, transparent 62%)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(120% 90% at 50% -10%, rgba(125,177,255,0.05), transparent 55%), linear-gradient(180deg, rgba(0,0,0,0.28), transparent 32%, transparent 68%, rgba(0,0,0,0.45))',
          }}
        />
      </div>

      <div className="relative z-[2] mx-4 flex w-full max-w-[480px] flex-col p-[32px]">
        <div className="mb-5">
          <BrandMark size="md" showStratiumLine inlineStratiumTagline accent="light" />
        </div>

        {lookupState === 'loading' && (
          <div className="flex h-[200px] items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[rgba(234,238,246,0.40)]" />
          </div>
        )}

        {lookupState === 'success' && session && (
          <>
            <CheckCircle2 className="mb-4 h-10 w-10 text-emerald-400" strokeWidth={1.5} />
            <h1 className="mb-[10px] font-serif text-[33px] font-light leading-[1.12] tracking-[-0.01em] text-white">
              Welcome to Cadence.
            </h1>
            <p className="mb-[30px] text-[15px] font-light tracking-wide text-[rgba(234,238,246,0.62)]">
              Your trial has started. Check your inbox to set your password and sign in.
            </p>

            {/* Receipt block — confirms what they bought + a short
                reference they can quote to support if the welcome
                email goes astray. */}
            <div className="mb-6 flex flex-col gap-3 rounded-[11px] border border-[rgba(234,238,246,0.12)] bg-[rgba(234,238,246,0.04)] p-5">
              {session.planName && (
                <Row label="Plan" value={session.planName} />
              )}
              {session.seatCount > 0 && (
                <Row label="Executive seats" value={`${session.seatCount}`} />
              )}
              {session.organisationName && (
                <Row label="Organisation" value={session.organisationName} />
              )}
              {session.maskedEmail && (
                <Row label="Welcome email sent to" value={session.maskedEmail} icon />
              )}
              <Row label="Reference" value={session.reference} mono />
            </div>

            <p className="mb-6 text-[13px] text-[rgba(234,238,246,0.62)]">
              The link in the welcome email is valid for 24 hours. If it expires before you click it, you can request a new one from the sign-in page.
            </p>

            <Button
              onClick={() => { window.location.href = '/login'; }}
              className="h-12 w-full rounded-[11px] bg-[rgba(234,238,246,0.04)] text-[15px] font-medium text-[#EAEEF6] hover:bg-[rgba(234,238,246,0.08)]"
              variant="outline"
            >
              Already set your password? Sign in
            </Button>
          </>
        )}

        {(lookupState === 'failed' || lookupState === 'no-id') && (
          <>
            <Mail className="mb-4 h-10 w-10 text-[#7DB1FF]" strokeWidth={1.5} />
            <h1 className="mb-[10px] font-serif text-[33px] font-light leading-[1.12] tracking-[-0.01em] text-white">
              {lookupState === 'no-id' ? 'Welcome to Cadence.' : "We're still processing your payment."}
            </h1>
            <p className="mb-[30px] text-[15px] font-light tracking-wide text-[rgba(234,238,246,0.62)]">
              {lookupState === 'no-id'
                ? 'Check your inbox to set your password and sign in.'
                : "Stripe is finishing up. You'll receive a welcome email shortly with a link to set your password — no need to do anything else."}
            </p>
            <Button
              onClick={() => { window.location.href = '/login'; }}
              className="h-12 w-full rounded-[11px] bg-[rgba(234,238,246,0.04)] text-[15px] font-medium text-[#EAEEF6] hover:bg-[rgba(234,238,246,0.08)]"
              variant="outline"
            >
              Already set your password? Sign in
            </Button>
          </>
        )}

        <p className="mt-8 text-center text-[11px] tracking-wide text-[rgba(234,238,246,0.30)]">
          Cadence is the product. Stratium is the company behind it.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, icon, mono }: { label: string; value: string; icon?: boolean; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={FIELD_LABEL_CLASS}>{label}</span>
      <span
        className={cn(
          'text-right text-[13.5px] text-[rgba(234,238,246,0.92)]',
          mono && 'font-mono text-[12px] tracking-[0.04em]',
          icon && 'flex items-center gap-1.5',
        )}
      >
        {icon && <Mail className="h-3.5 w-3.5 text-[rgba(234,238,246,0.40)]" />}
        {value}
      </span>
    </div>
  );
}
